// ── AI Activity Store ─────────────────────────────────────────────────────────
// Tracks virtual (client-side) activity runs for AI image generation.
// Merged into useActivity so GlobalActivityPill renders them transparently.
// Virtual run IDs are negative to avoid collision with server-side run IDs.

import { create } from 'zustand';
import type { ActivityRun } from '../hooks/useActivity.js';

type RunPatch = Partial<Pick<ActivityRun, 'label' | 'progress'>>;

interface AIActivityState {
  runs: ActivityRun[];
  /** Add a virtual run. Idempotent: replaces existing run with same id. */
  addRun: (run: ActivityRun) => void;
  /** Update label and/or progress of an existing virtual run. */
  updateRun: (id: number, patch: RunPatch) => void;
  /** Remove a virtual run by id. */
  removeRun: (id: number) => void;
}

export const useAIActivityStore = create<AIActivityState>((set) => ({
  runs: [],
  addRun: (run) =>
    set(s => ({ runs: [...s.runs.filter(r => r.id !== run.id), run] })),
  updateRun: (id, patch) =>
    set(s => ({ runs: s.runs.map(r => r.id === id ? { ...r, ...patch } : r) })),
  removeRun: (id) =>
    set(s => ({ runs: s.runs.filter(r => r.id !== id) })),
}));
